import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { LinkBadge, SuggestBadge, LINK_LABEL } from "./LegacyLinkBadge";

const STATUS_OPTS = [["open", "Perlu Diproses"], ["unprocessed", "Belum Diproses"], ["unknown", "Sumber Belum Ditautkan"],
  ["pending_verification", "Menunggu Verifikasi FAT"], ["linked_bank", "Tertaut ke Buku Bank"], ["linked_pc", "Tertaut ke Petty Cash"],
  ["linked_canonical", "Tertaut ke Transaksi Kanonis"], ["duplicate", "Duplikat"], ["not_project", "Bukan Pengeluaran Proyek"],
  ["cancelled", "Dibatalkan"], ["rejected", "Ditolak"], ["all", "Semua"]];
const SEL = "h-8 border border-slate-300 px-2 text-[11px] bg-white";

function Filters({ f, setF, projects, holders, banks }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="px-3 py-2 flex flex-wrap gap-2 items-center border-b border-slate-100" data-testid="lmq-filters">
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={f.q} onChange={set("q")} placeholder="Cari catatan, proyek, referensi…" data-testid="lmq-search" className={`${SEL} pl-6 w-56`} />
      </div>
      <select value={f.status} onChange={set("status")} data-testid="lmq-status" className={SEL}>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <select value={f.suggested} onChange={set("suggested")} data-testid="lmq-suggested" className={SEL}>
        <option value="">Semua saran sumber</option><option value="bank">Sumber Bank Disarankan</option>
        <option value="petty_cash">Petty Cash Disarankan</option><option value="unknown">Sumber Belum Diketahui</option>
      </select>
      <select value={f.project_id} onChange={set("project_id")} data-testid="lmq-project" className={`${SEL} max-w-[200px]`}>
        <option value="">Semua proyek</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={f.holder_id} onChange={set("holder_id")} data-testid="lmq-holder" className={SEL}>
        <option value="">Semua pemegang PC</option>{holders.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select>
      <select value={f.bank_account_id} onChange={set("bank_account_id")} data-testid="lmq-bank" className={SEL}>
        <option value="">Semua rekening</option>{banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} {b.account_no}</option>)}
      </select>
    </div>
  );
}

function Pager({ d, f, setF }) {
  return (
    <div className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-600 border-t border-slate-100" data-testid="lmq-pager">
      <span data-testid="lmq-total">{d.total} hasil · halaman {d.page} dari {d.pages}</span>
      <div className="inline-flex items-center gap-1">
        <select value={f.page_size} onChange={(e) => setF({ ...f, page_size: Number(e.target.value), page: 1 })} data-testid="lmq-page-size" className={SEL}>
          {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / halaman</option>)}
        </select>
        <button type="button" disabled={d.page <= 1} onClick={() => setF({ ...f, page: d.page - 1 })} data-testid="lmq-prev" className="p-1.5 border border-slate-300 disabled:opacity-40"><ChevronLeft size={12} /></button>
        <input value={f.page} onChange={(e) => setF({ ...f, page: Math.max(1, Math.min(d.pages, Number(e.target.value) || 1)) })} data-testid="lmq-page" className={`${SEL} w-12 text-center`} />
        <button type="button" disabled={d.page >= d.pages} onClick={() => setF({ ...f, page: d.page + 1 })} data-testid="lmq-next" className="p-1.5 border border-slate-300 disabled:opacity-40"><ChevronRight size={12} /></button>
      </div>
    </div>
  );
}

const TYPE = { petty_cash: "Petty Cash", bank: "Rekening Bank", unknown: "Belum diketahui" };

function Row({ e, canVerify, onAct }) {
  const canProcess = canVerify && ["unprocessed", "unknown"].includes(e.source_link_status) && !e.pc_realization_id;
  const canReopen = canVerify && !e.pc_realization_id && !["unprocessed", "unknown", "pending_verification"].includes(e.source_link_status);
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`pcm-legacy-row-${e.entry_id}`}>
      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(e.date)}</td>
      <td className="px-2 py-1.5">{e.project_name || "-"}{e.wo_number ? <div className="text-[10px] text-slate-500">{e.wo_number}</div> : null}</td>
      <td className="px-2 py-1.5 max-w-[260px]">{e.note || "—"}<div className="text-[10px] text-slate-500">{e.category || ""}{e.reference_no ? ` · ${e.reference_no}` : ""}{e.recorded_by_name ? ` · oleh ${e.recorded_by_name}` : ""}</div></td>
      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatIDR(e.amount)}</td>
      <td className="px-2 py-1.5 text-[11px] text-slate-600">{e.legacy_source || "—"}</td>
      <td className="px-2 py-1.5"><SuggestBadge s={e.suggestion} testid={`lmq-sug-${e.entry_id}`} /></td>
      <td className="px-2 py-1.5 text-[11px]">{TYPE[e.funding_source_type] || "—"}{e.holder_name ? <div className="text-[10px] text-slate-500">{e.holder_name}</div> : null}</td>
      <td className="px-2 py-1.5"><LinkBadge status={e.source_link_status} testid={`lmq-link-${e.entry_id}`} />{e.reason && <div className="text-[10px] text-slate-500 max-w-[160px]">{e.reason}</div>}</td>
      <td className="px-2 py-1.5 text-[11px]" data-testid={`lmq-verif-${e.entry_id}`}>{e.verification_label}</td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {canProcess && <button type="button" onClick={() => onAct({ kind: "legacy", e })} data-testid={`pcm-legacy-process-${e.entry_id}`} className="px-2 py-0.5 border border-[#0B1D3A] text-[#0B1D3A] text-[10px] uppercase tracking-wider font-semibold hover:bg-[#0B1D3A] hover:text-white">Proses</button>}
        {canReopen && <button type="button" onClick={() => onAct({ kind: "reopen", e })} data-testid={`pcm-legacy-reopen-${e.entry_id}`} className="px-2 py-0.5 border border-slate-300 text-slate-600 text-[10px] uppercase tracking-wider font-semibold">Buka Kembali</button>}
        {!canProcess && !canReopen && <span className="text-[10px] italic text-slate-400">{canVerify ? "—" : "Menunggu FAT"}</span>}
      </td>
    </tr>
  );
}

export default function LegacyQueue({ canVerify, onAct, reloadKey, onTotal }) {
  const [f, setF] = useState({ q: "", status: "open", suggested: "", project_id: "", holder_id: "", bank_account_id: "", page: 1, page_size: 25 });
  const [d, setD] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [opts, setOpts] = useState({ projects: [], holders: [], banks: [] });
  useEffect(() => {
    Promise.all([api.get("/active-projects").catch(() => ({ data: [] })), api.get("/pc-manual-realizations/funding-options").catch(() => ({ data: {} })), api.get("/company-bank-accounts").catch(() => ({ data: [] }))])
      .then(([p, h, b]) => setOpts({
        projects: (p.data || []).filter((x) => !x.parent_project_id).map((x) => ({ id: x.id, name: x.project_name })).sort((a, z) => (a.name || "").localeCompare(z.name || "")),
        holders: h.data.holders || [], banks: Array.isArray(b.data) ? b.data : (b.data.items || []),
      }));
  }, []);
  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const { data } = await api.get("/pc-manual-realizations-legacy/queue", { params });
      setD(data); onTotal?.(data.open_total);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat antrean"); }
  }, [f, onTotal]);
  useEffect(() => { load(); }, [load, reloadKey]);
  return (
    <div data-testid="lmq-panel">
      <p className="px-3 py-2 text-[10px] text-slate-500 border-b border-slate-100">
        Saran sumber hanya petunjuk dari data lama — FAT tetap memilih sumber sebenarnya. Data yang belum diproses tetap dihitung di Uang Keluar, tetapi <b>tidak masuk Cost Control</b>.
      </p>
      <Filters f={f} setF={setF} {...opts} />
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="pcm-legacy-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
            <tr className="text-left">
              {["Tgl Transaksi", "Proyek / WO", "Deskripsi", "Nominal", "Sumber Lama", "Saran Sumber", "Jenis Sumber Terpilih", "Status Tautan", "Status Verifikasi", "Aksi"].map((h) => (
                <th key={h} className={`px-2 py-1.5 ${h === "Nominal" || h === "Aksi" ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.items.length ? d.items.map((e) => <Row key={`${e.project_id}:${e.entry_id}`} e={e} canVerify={canVerify} onAct={onAct} />)
              : <tr><td colSpan={10} className="px-3 py-6 text-center italic text-slate-400" data-testid="pcm-legacy-empty">Tidak ada data pada filter ini{f.status !== "open" ? ` (${LINK_LABEL[f.status] || "Semua"})` : ""}.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager d={d} f={f} setF={setF} />
    </div>
  );
}
